import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Eye, Clock, Building2, Heart, Share2 } from 'lucide-react';
import { formatPrice, formatDate, truncateText } from '../lib/utils';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { addFavorite, removeFavorite, checkFavorite } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fix image URLs - convert /uploads/ to /api/uploads/
const fixImageUrl = (url) => {
    if (!url) return null;
    if (url.includes('/api/uploads/')) return url;
    if (url.includes('/uploads/') && !url.includes('/api/uploads/')) {
        const filename = url.split('/uploads/').pop();
        return `${BACKEND_URL}/api/uploads/${filename}`;
    }
    return url;
};

const ListingCard = ({ listing, showFavorite = true }) => {
    const { advertiser } = useAuth();
    const placeholderImage = `https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=300&fit=crop`;
    const [imgError, setImgError] = useState(false);
    const [isFavorited, setIsFavorited] = useState(false);
    const [favoriteLoading, setFavoriteLoading] = useState(false);
    
    const rawImageUrl = listing.images?.[0];
    const imageUrl = !imgError && rawImageUrl ? fixImageUrl(rawImageUrl) : placeholderImage;

    useEffect(() => {
        // Check if listing is favorited
        const checkFav = async () => {
            if (!advertiser || !showFavorite) return;
            try {
                const res = await checkFavorite(listing.id);
                setIsFavorited(res.data.favorited);
            } catch (err) {
                // Ignore errors for unauthenticated users
            }
        };
        checkFav();
    }, [advertiser, listing.id, showFavorite]);

    const handleFavoriteClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!advertiser) {
            toast.error('Please log in to save favorites');
            return;
        }
        
        setFavoriteLoading(true);
        try {
            if (isFavorited) {
                await removeFavorite(listing.id);
                setIsFavorited(false);
                toast.success('Removed from favorites');
            } else {
                await addFavorite(listing.id);
                setIsFavorited(true);
                toast.success('Added to favorites');
            }
        } catch (err) {
            toast.error('Failed to update favorites');
        } finally {
            setFavoriteLoading(false);
        }
    };

    const handleShareClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const shareUrl = `${window.location.origin}/listing/${listing.id}`;
        const shareText = `Check out this listing: ${listing.title}`;
        
        if (navigator.share) {
            try {
                await navigator.share({
                    title: listing.title,
                    text: shareText,
                    url: shareUrl
                });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    // Fallback to copy
                    copyToClipboard(shareUrl);
                }
            }
        } else {
            copyToClipboard(shareUrl);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success('Link copied to clipboard!');
        }).catch(() => {
            toast.error('Failed to copy link');
        });
    };

    return (
        <div className="listing-card block relative group" data-testid={`listing-card-${listing.id}`}>
            {/* Action Buttons */}
            {showFavorite && (
                <div className="absolute top-2 right-2 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                        size="sm"
                        variant="secondary"
                        className="bg-white/90 hover:bg-white shadow-sm h-8 w-8 p-0"
                        onClick={handleShareClick}
                        title="Share"
                    >
                        <Share2 className="w-4 h-4 text-gray-600" />
                    </Button>
                    <Button
                        size="sm"
                        variant="secondary"
                        className={`bg-white/90 hover:bg-white shadow-sm h-8 w-8 p-0 ${isFavorited ? 'text-red-500' : ''}`}
                        onClick={handleFavoriteClick}
                        disabled={favoriteLoading}
                        title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                    >
                        <Heart className={`w-4 h-4 ${isFavorited ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
                    </Button>
                </div>
            )}
            
            <Link to={`/listing/${listing.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                    <img
                        src={imageUrl}
                        alt={listing.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        onError={() => setImgError(true)}
                    />
                </div>
                <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary" className="text-xs">
                            {listing.category_name}
                        </Badge>
                        {listing.subcategory_name && (
                            <Badge variant="outline" className="text-xs">
                                {listing.subcategory_name}
                            </Badge>
                        )}
                    </div>
                    <h3 className="font-semibold text-lg text-navy mb-1 line-clamp-1">
                        {listing.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {truncateText(listing.description, 80)}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{listing.location}</span>
                        </div>
                        <span className="font-semibold text-gold text-base">
                            {formatPrice(listing.price)}
                        </span>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                        <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{formatDate(listing.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            <span>{listing.views} views</span>
                        </div>
                    </div>
                    {listing.business_name && (
                        <Link 
                            to={`/advertiser/${listing.advertiser_id}`}
                            className="flex items-center gap-1 mt-2 text-xs text-gray-500 hover:text-navy"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Building2 className="w-3 h-3" />
                            <span>{listing.business_name}</span>
                        </Link>
                    )}
                </div>
            </Link>
        </div>
    );
};

export default ListingCard;
